import { dashStart, type DashState } from '@/domain/hs1'

/**
 * Der Zustandshalter des Spiels — zwischen einem 60-Hz-Loop und React.
 *
 * DAS PROBLEM, DAS ER LÖST. Der Spielzustand ändert sich mit jedem Bild. Läge er
 * in `useState`, rechnete React sechzigmal je Sekunde die halbe Anwendung neu —
 * für eine Zahl, die man ohnehin nur zehnmal je Sekunde lesen kann. Läge er nur
 * in einer Referenz, sähe die Anzeige ihn nie.
 *
 * DIE LÖSUNG IST NICHT „SCHNELLER", SONDERN „SELTENER, ABER NIE ZU SPÄT".
 * Fortlaufende Grössen — Strecke, Hitze, Ladung — werden gedrosselt gemeldet;
 * sie ändern sich stetig, und ein Achtelsekunde alter Wert ist keine Falschaussage.
 * Sprunghafte Grössen — eine verlorene Hülle, das Spielende, eine Meldung — werden
 * SOFORT gemeldet. Ein Treffer, der mit achtzig Millisekunden Verspätung aufblitzt,
 * gehört gefühlt zum nächsten Hindernis.
 *
 * Der Bestwert ist das Einzige, was das Spiel überlebt. Er liegt im
 * `localStorage` und geht niemanden etwas an — kein Netz, keine Datenbank, kein
 * Konto. Für eine Spielerei wäre alles andere übergriffig.
 */

const BEST_KEY = 'neura.dash.best'

/** Höchstens so oft melden, solange sich nur Stetiges ändert. */
const THROTTLE_MS = 90

function readBest(): number {
  /* Ein privates Fenster oder gesperrte Site-Daten werfen hier. Der Bestwert
     ist kein Grund, das Spiel nicht zu starten. */
  try {
    const raw = window.localStorage.getItem(BEST_KEY)
    const value = raw === null ? 0 : Number(raw)
    return Number.isFinite(value) && value > 0 ? value : 0
  } catch {
    return 0
  }
}

function writeBest(value: number): void {
  try {
    window.localStorage.setItem(BEST_KEY, String(value))
  } catch {
    /* Absichtlich still: ein nicht gespeicherter Bestwert ist kein Fehler,
       den ein Spieler mitten im Lauf gemeldet bekommen möchte. */
  }
}

export interface DashStore {
  /** Der aktuelle Zustand. Wird vom Loop geschrieben und nie kopiert. */
  get: () => DashState
  /** Setzt den Zustand und meldet ihn nach den Regeln oben. */
  set: (next: DashState) => void
  subscribe: (fn: () => void) => () => void
  /** Startet neu und behält den Bestwert. */
  restart: () => void
  /** Beendet und räumt auf. */
  stop: () => void
  /** Startet aus dem Ruhezustand. */
  begin: () => void
  best: () => number
}

/** Der Ruhezustand: kein Spiel. */
const idle = (best: number): DashState => ({ ...dashStart(best), running: false })

export function createDashStore(): DashStore {
  let best = readBest()
  let state = idle(best)
  const listeners = new Set<() => void>()
  let lastNotify = 0
  let timer: number | null = null

  const emit = () => {
    lastNotify = performance.now()
    if (timer !== null) {
      window.clearTimeout(timer)
      timer = null
    }
    for (const fn of listeners) fn()
  }

  const notify = (immediate: boolean) => {
    if (immediate) {
      emit()
      return
    }
    const due = THROTTLE_MS - (performance.now() - lastNotify)
    if (due <= 0) {
      emit()
    } else if (timer === null) {
      timer = window.setTimeout(emit, due)
    }
  }

  const set = (next: DashState) => {
    const before = state
    state = next
    if (next.best > best) {
      best = next.best
      writeBest(best)
    }
    /* Was sprunghaft ist, geht sofort durch. Alles andere darf warten. */
    const abrupt =
      next.lives !== before.lives ||
      next.over !== before.over ||
      next.running !== before.running ||
      next.hot !== before.hot ||
      (next.message !== '' && next.message !== before.message)
    notify(abrupt)
  }

  return {
    get: () => state,
    set,
    subscribe: (fn) => {
      listeners.add(fn)
      return () => {
        listeners.delete(fn)
      }
    },
    begin: () => {
      state = dashStart(best)
      notify(true)
    },
    restart: () => {
      state = dashStart(best)
      notify(true)
    },
    stop: () => {
      state = idle(best)
      notify(true)
    },
    best: () => best,
  }
}
